import { FormLabel, Select } from "@oysterhr/ui"
import { Controller, useFormContext } from "react-hook-form"
import { PayrollChangeFormValues } from "../types"
import { useTranslation } from "react-i18next"
import { PaymentSchedule } from "../constants"
import { useEffect, useMemo } from "react"
import { getEndingMonthOptions, getMonthOptions } from "../utils"
import { Engagement } from "@oysterhr/schema"

interface Props {
  isDateReadOnly: boolean
  isPaymentScheduleReadOnly: boolean
  engagement?: Engagement
}

export const PaymentScheduleFields = ({
  isDateReadOnly,
  isPaymentScheduleReadOnly,
  engagement,
}: Props) => {
  const { control, watch, setValue, getValues } = useFormContext<PayrollChangeFormValues>()
  const { t } = useTranslation("Payroll")
  const selectedEngagementId = watch("selectedEngagementId")
  const startingMonth = watch("startingMonth")
  const paymentSchedule = watch("paymentSchedule")
  const paymentSheduleOptions = [
    { label: t("form.paymentScheduleOptions.oneTime"), value: PaymentSchedule.OneTime },
    { label: t("form.paymentScheduleOptions.recurring"), value: PaymentSchedule.Recurring },
  ]
  const monthOptions = useMemo(
    () =>
      getMonthOptions({
        nextCutoffTime: engagement?.nextCutoffTime,
        startDate: engagement?.employment?.startDate || engagement?.contract?.startDate,
        terminationDate:
          engagement?.employment?.terminationDate || engagement?.contract?.terminationDate,
      }),
    [engagement],
  )
  const endingMonthOptions = useMemo(
    () =>
      getEndingMonthOptions({
        startingMonth,
        terminationDate:
          engagement?.employment?.terminationDate || engagement?.contract?.terminationDate,
      }),
    [engagement?.contract?.terminationDate, engagement?.employment?.terminationDate, startingMonth],
  )

  // Reset `month` on `monthOptions` change, if it's a disabled month
  useEffect(() => {
    const { month, paymentSchedule } = getValues()
    if (!paymentSchedule || !monthOptions) return
    const isMonthDisabled = !monthOptions?.find(({ value }) => value === month)
    if (isMonthDisabled) {
      setValue("month", "")
    }
  }, [paymentSchedule, monthOptions, setValue, getValues])

  return (
    <>
      <Controller
        name="paymentSchedule"
        control={control}
        render={({ field: { ref: _ref, ...field }, fieldState: { error } }) => (
          <Select
            label={<FormLabel label={t("form.label.paymentSchedule")} htmlFor={field.name} />}
            error={error?.message}
            placeholder={t("form.placeholders.paymentSchedule")}
            disabled={!selectedEngagementId || isPaymentScheduleReadOnly}
            {...field}
          >
            {paymentSheduleOptions.map(({ label, value }) => (
              <Select.Option key={value} value={value}>
                {label}
              </Select.Option>
            ))}
          </Select>
        )}
      />
      {paymentSchedule === PaymentSchedule.OneTime && (
        <Controller
          name="month"
          control={control}
          render={({ field: { ref: _ref, ...field }, fieldState: { error } }) => (
            <Select
              label={<FormLabel label={t("form.label.month")} htmlFor={field.name} />}
              error={error?.message}
              placeholder={t("form.placeholders.month")}
              disabled={!selectedEngagementId || isDateReadOnly}
              {...field}
            >
              {monthOptions?.map(({ label, value }) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          )}
        />
      )}
      {paymentSchedule === PaymentSchedule.Recurring && (
        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="startingMonth"
            control={control}
            render={({ field: { onChange, ref: _ref, ...field }, fieldState: { error } }) => (
              <Select
                label={<FormLabel label={t("form.label.startingMonth")} htmlFor={field.name} />}
                error={error?.message}
                placeholder={t("form.placeholders.startingMonth")}
                disabled={!selectedEngagementId}
                onChange={value => {
                  setValue("endingMonth", "")
                  onChange(value)
                }}
                {...field}
              >
                {monthOptions?.map(({ label, value }) => (
                  <Select.Option key={value} value={value}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
          <Controller
            name="endingMonth"
            control={control}
            render={({ field: { ref: _ref, ...field }, fieldState: { error } }) => (
              <Select
                label={<FormLabel label={t("form.label.endingMonth")} htmlFor={field.name} />}
                error={error?.message}
                placeholder={t("form.placeholders.endingMonth")}
                disabled={!selectedEngagementId}
                {...field}
              >
                {endingMonthOptions?.map(({ label, value }) => (
                  <Select.Option key={value} value={value}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
        </div>
      )}
    </>
  )
}
